import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { usePerformanceScope } from '@/lib/use-performance-scope'
import { cn } from '@/lib/utils'
import {
  ALL_LAYERS,
  CITY_COORDS,
  LAYER_COLOURS,
  LAYER_LABELS,
  MILES_TO_KM,
  RADIUS_OPTIONS,
  WORLD_MAP_CENTER,
  WORLD_MAP_ZOOM,
  fitMapToFanDemandPins,
  distanceKm,
} from '@/pages/territory-map-helpers'
import type {
  LayerName,
  LegOption,
  MapData,
  TourOption,
} from '@/pages/territory-map-helpers'
import {
  renderBrokers,
  renderCrew,
  renderInquiries,
  renderShows,
  renderVenues,
  renderFanDemand,
} from '@/pages/territory-map-renderers'

interface TerritoryMapProps {
  initialLayers?: Set<LayerName> | null
  /** Dashboard embed: always load fan demand, ignore sidebar tour scope by default */
  variant?: 'territory' | 'dashboard'
}

function withFanDemandLayer(layers: Set<LayerName>): Set<LayerName> {
  if (layers.has('fan_demand')) return layers
  return new Set([...layers, 'fan_demand'])
}

export function TerritoryMap({
  initialLayers = null,
  variant = 'territory',
}: TerritoryMapProps) {
  const scope = usePerformanceScope()
  const mapInstance = useRef<L.Map | null>(null)
  const [mapHost, setMapHost] = useState<HTMLDivElement | null>(null)
  const radiusCircleRef = useRef<L.Circle | null>(null)
  const ignoreScope = variant === 'dashboard'

  const [activeLayers, setActiveLayers] = useState<Set<LayerName>>(() => {
    const base = initialLayers ?? new Set(ALL_LAYERS)
    return variant === 'dashboard' ? withFanDemandLayer(base) : base
  })
  const [selectedTourId, setSelectedTourId] = useState('')
  const [selectedLegId, setSelectedLegId] = useState('')
  const [selectedShowId, setSelectedShowId] = useState('')
  const [radiusCentre, setRadiusCentre] = useState<[number, number] | null>(null)
  const [radiusMiles, setRadiusMiles] = useState(50)
  const [nearCity, setNearCity] = useState('')

  const initialLayersKey = useMemo(
    () =>
      initialLayers
        ? Array.from(initialLayers).sort().join(',')
        : '',
    [initialLayers],
  )

  useEffect(() => {
    if (!initialLayersKey || !initialLayers) return
    setActiveLayers(
      variant === 'dashboard'
        ? withFanDemandLayer(initialLayers)
        : initialLayers,
    )
  }, [initialLayersKey, initialLayers, variant])

  useEffect(() => {
    if (ignoreScope || !scope.active) return
    if (scope.kind === 'tour') {
      setSelectedTourId(scope.id ?? '')
      setSelectedLegId('')
      setSelectedShowId('')
    } else if (scope.kind === 'show') {
      setSelectedShowId(scope.id ?? '')
    }
  }, [ignoreScope, scope.active, scope.kind, scope.id])

  const layerParam = Array.from(activeLayers).join(',')
  const queryParams = new URLSearchParams({ layers: layerParam })
  if (selectedTourId) queryParams.set('tourId', selectedTourId)
  else if (!ignoreScope && scope.tourId) queryParams.set('tourId', scope.tourId)
  if (selectedLegId) queryParams.set('legId', selectedLegId)
  if (selectedShowId) queryParams.set('showId', selectedShowId)
  else if (!ignoreScope && scope.showId) queryParams.set('showId', scope.showId)

  const { data: mapData, error, isLoading } = useQuery<MapData, ApiClientError>({
    queryKey: [
      'territory-map',
      variant,
      layerParam,
      selectedTourId,
      selectedLegId,
      selectedShowId,
      ...(ignoreScope ? (['global'] as const) : scope.queryKeySuffix),
    ],
    queryFn: () => artistFetch<MapData>(`/territory/map-data?${queryParams.toString()}`),
  })

  // Dashboard always fetches fan demand even if the layer toggle is off
  const { data: fanDemandOnly } = useQuery<MapData, ApiClientError>({
    queryKey: ['territory-map-fan-demand', variant],
    queryFn: () =>
      artistFetch<MapData>('/territory/map-data?layers=fan_demand'),
    enabled: variant === 'dashboard',
    staleTime: 60_000,
  })

  const resolvedMapData = useMemo((): MapData | undefined => {
    if (!mapData && !fanDemandOnly) return undefined
    const fan =
      mapData?.fan_demand?.length
        ? mapData.fan_demand
        : fanDemandOnly?.fan_demand
    return { ...mapData, ...(fan ? { fan_demand: fan } : {}) }
  }, [mapData, fanDemandOnly])

  const { data: tours } = useQuery<TourOption[]>({
    queryKey: ['tours-list'],
    queryFn: () => artistFetch<TourOption[]>('/tours'),
  })

  const { data: legs } = useQuery<LegOption[]>({
    queryKey: ['tour-legs', selectedTourId],
    queryFn: () => artistFetch<LegOption[]>(`/tours/${selectedTourId}/legs`),
    enabled: !!selectedTourId,
  })

  const toggleLayer = useCallback((layer: LayerName) => {
    setActiveLayers((prev) => {
      const next = new Set(prev)
      if (next.has(layer)) {
        if (variant === 'dashboard' && layer === 'fan_demand') return prev
        next.delete(layer)
      } else {
        next.add(layer)
      }
      return next
    })
  }, [variant])

  const handleNearCitySearch = useCallback(() => {
    if (!nearCity.trim()) { setRadiusCentre(null); return }
    const coords = CITY_COORDS[nearCity.trim().toLowerCase()]
    if (coords) setRadiusCentre(coords)
  }, [nearCity])

  const handleReset = useCallback(() => {
    const layers = variant === 'dashboard' ? withFanDemandLayer(new Set(ALL_LAYERS)) : new Set(ALL_LAYERS)
    setActiveLayers(layers)
    setSelectedTourId('')
    setSelectedLegId('')
    setSelectedShowId('')
    setRadiusCentre(null)
    setNearCity('')
    setRadiusMiles(50)
    mapInstance.current?.setView(WORLD_MAP_CENTER, WORLD_MAP_ZOOM)
  }, [variant])

  const radiusKm = useMemo(() => radiusMiles * MILES_TO_KM, [radiusMiles])

  const isWithinRadius = useCallback(
    (lat: number, lng: number): boolean => {
      if (!radiusCentre) return true
      return distanceKm(radiusCentre[0], radiusCentre[1], lat, lng) <= radiusKm
    },
    [radiusCentre, radiusKm],
  )

  useEffect(() => {
    if (!mapHost || mapInstance.current) return
    const map = L.map(mapHost).setView(WORLD_MAP_CENTER, WORLD_MAP_ZOOM)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map)
    map.on('click', (e: L.LeafletMouseEvent) => {
      setRadiusCentre([e.latlng.lat, e.latlng.lng])
      setNearCity('')
    })
    mapInstance.current = map
    const invalidate = () => map.invalidateSize()
    setTimeout(invalidate, 100)
    setTimeout(invalidate, 500)
    setTimeout(invalidate, 1500)
    window.addEventListener('resize', invalidate)
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => invalidate())
      : null
    ro?.observe(mapHost)
    return () => {
      window.removeEventListener('resize', invalidate)
      ro?.disconnect()
      map.remove()
      mapInstance.current = null
    }
  }, [mapHost])

  useEffect(() => {
    const map = mapInstance.current
    if (!map || !resolvedMapData) return
    map.invalidateSize()
    map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) map.removeLayer(layer)
    })
    radiusCircleRef.current = null

    if (radiusCentre) {
      radiusCircleRef.current = L.circle(radiusCentre, {
        radius: radiusKm * 1000,
        color: '#6366f1',
        fillColor: '#6366f1',
        fillOpacity: 0.08,
        weight: 2,
        dashArray: '6 4',
      }).addTo(map)
    }

    const layersForRender =
      variant === 'dashboard'
        ? withFanDemandLayer(activeLayers)
        : activeLayers

    renderBrokers(map, resolvedMapData, layersForRender, isWithinRadius)
    renderInquiries(map, resolvedMapData, layersForRender, isWithinRadius)
    renderShows(map, resolvedMapData, layersForRender, isWithinRadius)
    renderCrew(map, resolvedMapData, layersForRender, isWithinRadius)
    renderVenues(map, resolvedMapData, layersForRender, isWithinRadius)
    renderFanDemand(map, resolvedMapData, layersForRender, isWithinRadius)

    if (
      layersForRender.has('fan_demand') &&
      !radiusCentre &&
      resolvedMapData.fan_demand?.length
    ) {
      fitMapToFanDemandPins(map, resolvedMapData.fan_demand)
    }
  }, [resolvedMapData, activeLayers, radiusCentre, radiusKm, isWithinRadius, variant])

  return (
    <div className="space-y-4">
      {variant === 'dashboard' && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-display text-xl">Territory map</h2>
            <p className="text-sm text-ovation-muted">
              Fan concert requests from Velvet Rope appear as{' '}
              <span className="font-medium text-sky-600">blue counted pins</span>
              {resolvedMapData?.fan_demand?.length
                ? ` (${resolvedMapData.fan_demand.length} cities)`
                : !isLoading
                  ? ' — none loaded yet; sync Velvet Rope under Integrations'
                  : ''}
              .
            </p>
          </div>
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 break-words">
          {error.message}
        </div>
      )}
      <div className="flex flex-col gap-4 lg:flex-row">
        <ControlPanel
          activeLayers={activeLayers}
          toggleLayer={toggleLayer}
          lockFanDemand={variant === 'dashboard'}
          selectedTourId={selectedTourId}
          setSelectedTourId={(v) => { setSelectedTourId(v); setSelectedLegId(''); setSelectedShowId('') }}
          selectedLegId={selectedLegId}
          setSelectedLegId={(v) => { setSelectedLegId(v); setSelectedShowId('') }}
          selectedShowId={selectedShowId}
          setSelectedShowId={setSelectedShowId}
          tours={tours ?? []}
          legs={legs ?? []}
          shows={resolvedMapData?.shows ?? []}
          nearCity={nearCity}
          setNearCity={setNearCity}
          handleNearCitySearch={handleNearCitySearch}
          radiusCentre={radiusCentre}
          radiusMiles={radiusMiles}
          setRadiusMiles={setRadiusMiles}
          handleReset={handleReset}
        />
        <div className="relative min-h-[600px] flex-1">
          <div
            ref={setMapHost}
            className="absolute inset-0 rounded-lg border border-ovation-ink/10"
          />
          {isLoading && (
            <div className="absolute inset-0 z-[500] flex items-center justify-center rounded-lg bg-white/60 text-sm text-ovation-muted">
              Loading map data…
            </div>
          )}
          <Legend activeLayers={activeLayers} radiusCentre={radiusCentre} radiusMiles={radiusMiles} />
        </div>
      </div>
    </div>
  )
}

interface ControlPanelProps {
  activeLayers: Set<LayerName>
  toggleLayer: (l: LayerName) => void
  lockFanDemand?: boolean
  selectedTourId: string
  setSelectedTourId: (v: string) => void
  selectedLegId: string
  setSelectedLegId: (v: string) => void
  selectedShowId: string
  setSelectedShowId: (v: string) => void
  tours: TourOption[]
  legs: LegOption[]
  shows: { id: string; title: string }[]
  nearCity: string
  setNearCity: (v: string) => void
  handleNearCitySearch: () => void
  radiusCentre: [number, number] | null
  radiusMiles: number
  setRadiusMiles: (v: number) => void
  handleReset: () => void
}

function ControlPanel(props: ControlPanelProps) {
  return (
    <div className="w-72 shrink-0 space-y-5 overflow-y-auto rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">Layers</h3>
        <div className="space-y-1.5">
          {(Object.entries(LAYER_LABELS) as [LayerName, string][]).map(([key, label]) => (
            <label key={key} className={cn(
              'flex items-center gap-2 rounded px-2 py-1',
              props.lockFanDemand && key === 'fan_demand'
                ? 'cursor-default opacity-90'
                : 'cursor-pointer hover:bg-ovation-ink/5',
            )}>
              <input
                type="checkbox"
                checked={props.activeLayers.has(key)}
                disabled={props.lockFanDemand && key === 'fan_demand'}
                onChange={() => props.toggleLayer(key)}
                className="h-3.5 w-3.5 rounded border-gray-300"
              />
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: LAYER_COLOURS[key] }} />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">Filters</h3>
        <div className="space-y-2">
          <FilterSelect label="Tour" value={props.selectedTourId} onChange={props.setSelectedTourId} placeholder="All tours" options={props.tours.map((t) => ({ value: t.id, label: t.name }))} />
          {props.selectedTourId && (
            <FilterSelect label="Leg" value={props.selectedLegId} onChange={props.setSelectedLegId} placeholder="All legs" options={props.legs.map((l) => ({ value: l.id, label: l.name }))} />
          )}
          <FilterSelect label="Show" value={props.selectedShowId} onChange={props.setSelectedShowId} placeholder="All shows" options={props.shows.map((s) => ({ value: s.id, label: s.title }))} />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">Radius</h3>
        <div className="space-y-2">
          <div className="flex gap-1">
            <input type="text" placeholder="City name..." value={props.nearCity} onChange={(e) => props.setNearCity(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') props.handleNearCitySearch() }} className="min-w-0 flex-1 rounded border border-ovation-ink/15 px-2 py-1.5 text-sm" />
            <button type="button" onClick={props.handleNearCitySearch} className="shrink-0 rounded bg-ovation-ink/10 px-2 py-1.5 text-xs hover:bg-ovation-ink/20">Set</button>
          </div>
          <p className="text-xs text-ovation-muted">Or click the map to set centre.</p>
          {props.radiusCentre && (
            <>
              <div>
                <label className="mb-0.5 block text-xs text-ovation-muted">Distance: {props.radiusMiles} miles</label>
                <input type="range" min={10} max={200} step={5} value={props.radiusMiles} onChange={(e) => props.setRadiusMiles(parseInt(e.target.value, 10))} className="w-full" />
                <div className="flex justify-between text-[10px] text-ovation-muted"><span>10 mi</span><span>200 mi</span></div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {RADIUS_OPTIONS.map((opt) => (
                  <button key={opt.miles} type="button" onClick={() => props.setRadiusMiles(opt.miles)} className={`rounded px-2 py-0.5 text-xs ${props.radiusMiles === opt.miles ? 'bg-ovation-ink text-white' : 'bg-ovation-ink/10 hover:bg-ovation-ink/20'}`}>{opt.label}</button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <button type="button" onClick={props.handleReset} className="w-full rounded border border-ovation-ink/15 px-3 py-1.5 text-sm text-ovation-muted hover:bg-ovation-ink/5">Show all</button>
    </div>
  )
}

function FilterSelect(props: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
  options: { value: string; label: string }[]
}) {
  return (
    <div>
      <label className="mb-0.5 block text-xs text-ovation-muted">{props.label}</label>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)} className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm">
        <option value="">{props.placeholder}</option>
        {props.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function Legend(props: {
  activeLayers: Set<LayerName>
  radiusCentre: [number, number] | null
  radiusMiles: number
}) {
  return (
    <div className="absolute bottom-4 right-4 z-[1000] rounded-lg border border-ovation-ink/10 bg-white/95 px-3 py-2 shadow-md backdrop-blur-sm">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ovation-muted">Legend</div>
      <div className="space-y-0.5">
        {(Object.entries(LAYER_LABELS) as [LayerName, string][])
          .filter(([key]) => props.activeLayers.has(key))
          .map(([key, label]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: LAYER_COLOURS[key] }} />
              <span className="text-xs">{label}</span>
            </div>
          ))}
      </div>
      {props.radiusCentre && (
        <div className="mt-1 flex items-center gap-1.5 border-t border-ovation-ink/10 pt-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: '#6366f1' }} />
          <span className="text-xs">{props.radiusMiles} mi radius</span>
        </div>
      )}
    </div>
  )
}
